import { useMemo, useState } from "react";
import type { Crm } from "../lib/crm";
import { countChanges, diffConfig, GATE_LABELS, labelInSection, listChange, sectionsFor, type DiffRow } from "../lib/config";
import type { ConfigVersion, JsonObject } from "../lib/types";
import { fmtDateTime } from "../lib/format";
import { ReasonDialog, Notice } from "./ui";

function GateList({ gates }: { gates: string[] }) {
  if (!gates.length) return <p className="muted">No approval gates: everything runs without asking you.</p>;
  return (
    <ul className="plain-list">
      {gates.map((g) => (
        <li key={g}>
          <strong>{GATE_LABELS[g] ?? `Step ${g}`}</strong> <span className="muted">(step {g})</span>
        </li>
      ))}
    </ul>
  );
}

const HEX = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i;

/** A value as text, with a colour swatch for brand colours. */
function Value({ v }: { v: string | null }) {
  if (v && HEX.test(v)) {
    return (
      <span className="swatch-line">
        <span className="swatch" style={{ background: v }} aria-hidden="true" />
        {v}
      </span>
    );
  }
  return <>{v}</>;
}

function Changed({ r }: { r: DiffRow }) {
  const list = r.list ? listChange(r.before, r.after) : null;
  if (list) {
    return (
      <ul className="diff-list">
        {list.kept.map((x, i) => (
          <li key={`k-${i}-${x}`}>{x}</li>
        ))}
        {list.added.map((x, i) => (
          <li key={`a-${i}-${x}`}>
            <ins>
              <span className="sr-only">Added: </span>
              {x}
            </ins>
          </li>
        ))}
        {list.removed.map((x, i) => (
          <li key={`r-${i}-${x}`}>
            <del>
              <span className="sr-only">Removed: </span>
              {x}
            </del>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <span className="diff-pair">
      <del>
        <span className="sr-only">Was: </span>
        <Value v={r.before} />
      </del>
      <span aria-hidden="true" className="diff-arrow">
        to
      </span>
      <ins>
        <span className="sr-only">Now: </span>
        <Value v={r.after} />
      </ins>
    </span>
  );
}

function Rows({ rows, showChanges, section }: { rows: DiffRow[]; showChanges: boolean; section: string }) {
  if (!rows.length) return <p className="muted">Nothing set in this section yet.</p>;
  return (
    <dl className="kv kv-diff">
      {rows.map((r) => (
        <div key={r.path} className={`kv-row${showChanges ? ` diff-${r.change}` : ""}`}>
          <dt>
            {labelInSection(r.label, section)}
            {showChanges && r.change !== "same" && <span className={`diff-tag diff-tag-${r.change}`}>{r.change === "added" ? "New" : r.change === "removed" ? "Removed" : "Changed"}</span>}
          </dt>
          <dd>
            {showChanges && r.change === "changed" && <Changed r={r} />}
            {showChanges && r.change === "removed" && (
              <del>
                <Value v={r.before} />
              </del>
            )}
            {(!showChanges || r.change === "same" || r.change === "added") && <Value v={r.after} />}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** A settings version laid out in readable sections; with `before`, differences are marked. */
export function ConfigSections({ config, before, onlyChanges = false }: { config: JsonObject; before: JsonObject | null; onlyChanges?: boolean }) {
  const sections = sectionsFor(config).map(({ def }) => {
    const all = diffConfig(before, config, def.keys);
    return { def, all, changes: countChanges(all) };
  });
  const filtering = onlyChanges && Boolean(before);
  const shown = filtering ? sections.filter((s) => s.changes > 0) : sections;
  const hidden = sections.length - shown.length;
  return (
    <div className="sections">
      {shown.map(({ def, all, changes }) => {
        const rows = filtering ? all.filter((r) => r.change !== "same") : all;
        const gates = def.id === "gates" && Array.isArray(config.human_gates) ? (config.human_gates as string[]) : null;
        const rest = gates ? rows.filter((r) => !r.path.startsWith("human_gates")) : rows;
        return (
          <section key={def.id} className="section" aria-labelledby={`sec-${def.id}`}>
            <div className="section-head">
              <h3 id={`sec-${def.id}`}>{def.title}</h3>
              {before && <span className={`chip ${changes ? "chip-warn" : "chip-plain"}`}>{changes ? `${changes} ${changes === 1 ? "change" : "changes"}` : "No changes"}</span>}
            </div>
            {!filtering && <p className="muted">{def.blurb}</p>}
            {gates && !filtering && <GateList gates={gates} />}
            {(rest.length > 0 || !gates) && <Rows rows={rest} showChanges={Boolean(before)} section={def.title} />}
          </section>
        );
      })}
      {filtering && hidden > 0 && (
        <p className="muted small sections-note">
          {hidden} other {hidden === 1 ? "section is" : "sections are"} unchanged. Untick "Show only what changed" to read the whole playbook.
        </p>
      )}
    </div>
  );
}

export function PlaybookReview({ crm, draft, active, onDecided }: { crm: Crm; draft: ConfigVersion; active: JsonObject | null; onDecided: () => void }) {
  const hasActive = Boolean(active && Object.keys(active).length);
  const [onlyChanges, setOnlyChanges] = useState(hasActive);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const total = useMemo(() => (hasActive ? countChanges(diffConfig(active, draft.config)) : 0), [active, draft, hasActive]);

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      await crm.decideConfig(draft.version, true);
      onDecided();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="playbook">
      <div className="playbook-meta">
        <p>
          Version {draft.version}, drafted by {draft.created_by.replace(/-agent$/, " agent")} on {fmtDateTime(draft.created_at)}.
          {hasActive ? ` ${total} ${total === 1 ? "difference" : "differences"} from the version in use.` : " This is the first version."}
        </p>
        {draft.change_note && <p className="quote">{draft.change_note}</p>}
        {hasActive && (
          <label className="check">
            <input type="checkbox" checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)} /> Show only what changed
          </label>
        )}
      </div>
      <ConfigSections config={draft.config} before={hasActive ? active : null} onlyChanges={onlyChanges} />
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button type="button" className="btn" onClick={() => setAsking(true)} disabled={busy}>
          Request changes
        </button>
        <button type="button" className="btn btn-primary" onClick={approve} disabled={busy}>
          {busy ? "Approving…" : "Approve playbook"}
        </button>
      </div>
      {asking && (
        <ReasonDialog
          title="Request changes"
          label="What should change?"
          confirm="Send to the strategy agent"
          onClose={() => setAsking(false)}
          onConfirm={async (reason) => {
            await crm.requestPlaybookChanges(draft.version, reason);
            onDecided();
          }}
        >
          <p className="muted">This version is set aside and the strategy agent drafts a new one with your comments.</p>
        </ReasonDialog>
      )}
    </div>
  );
}
